import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";

import { cancelExpenseClaim } from "../../../api/dossier/cancelExpenseClaim";
import { getAwaitingExpenseClaims } from "../../../api/dossier/getAwaitingExpenseClaims";
import {
  getResolvedExpenseClaims,
  ResolvedExpenseClaimFilters,
} from "../../../api/dossier/getResolvedExpenseClaims";
import { getMyBankAccount } from "../../../api/user/getMyBankAccount";
import APFilterableList from "../../../components/APFilterableList";
import APModal from "../../../components/APModal";
import APButton from "../../../components/buttons/APButton";
import DossierSelector from "../../../components/dossier/DossierSelector";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../../components/form/APAutoComplete";
import APDateField from "../../../components/form/APDateField";
import { isDossierActive } from "../../../helpers/dossiers";
import {
  calculatePendingClaimBalance,
  filterExpenseClaims,
  sortExpenseClaimsByStatus,
} from "../../../helpers/expenseClaims";
import { useDossierActivationCheck } from "../../../hooks/useDossierActivationCheck";
import { useFaqs } from "../../../hooks/useFaqs";
import { useAuth } from "../../../providers/AuthProvider";
import {
  ExpenseClaim,
  ExpenseClaimType,
  ExpenseClaimTypeOptions,
} from "../../../types/ExpenseClaim";
import ActiveDossierNavButtons from "../components/ActiveDossierNavButtons";
import DossierBalances from "../components/DossierBalances";
import ExpenseClaimCard from "../components/ExpenseClaimCard";

type ExpenseClaimFilterInputs = {
  dateFrom: string;
  dateTo: string;
  category: AutoCompleteOption[] | null;
};

export const DEFAULT_PAGE_SIZE = 15;

const DossierExpenseClaims = () => {
  const { inAppScope } = useAuth();
  const { dossierId } = useParams<{
    dossierId: string;
  }>();
  const [searchParams] = useSearchParams();
  const shouldPoll = !!searchParams.get("pollClaims");
  const [cancellingClaimId, setCancellingClaimId] = useState<string | null>(
    null
  );
  const [isPollingAwaitingExpenseClaims, setIsPollingAwaitingExpenseClaims] =
    useState(shouldPoll);
  const [filters, setFilters] = useState<ResolvedExpenseClaimFilters>({
    dossierId: dossierId || "",
    pageSize: DEFAULT_PAGE_SIZE,
    claimTypes: ["fuel", "servicing", "tyres", "rego", "insurance"],
  });

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<ExpenseClaimFilterInputs>({
    mode: "onTouched",
  });

  const dateFrom = watch("dateFrom");
  const dateTo = watch("dateTo");

  const { navigationCheckComplete, isLoading, isSuccess, activatedDossiers } =
    useDossierActivationCheck({
      dossierId,
    });
  const selectedDossier = activatedDossiers.find(
    (dossier) => dossier.id === dossierId
  );
  const { faqProps, isSuccess: isSuccessFaqs } = useFaqs({
    pageId: "expense-claims-view",
    contextIds: {
      opportunityId: selectedDossier?.opportunity?.id,
    },
  });
  const awaitingExpenseClaims = getAwaitingExpenseClaims(dossierId ?? "", {
    refetchInterval: isPollingAwaitingExpenseClaims ? 5000 : undefined,
  });
  const bankAccount = getMyBankAccount({ disabled: inAppScope("internal") });
  const {
    mutateAsync: _cancelExpenseClaim,
    isLoading: isCancellingExpenseClaim,
  } = cancelExpenseClaim(dossierId ?? "", {
    mutationOptions: {
      successAlertMessage: "Expense claim has been cancelled",
    },
  });

  if (awaitingExpenseClaims.isError && !awaitingExpenseClaims.isSuccess) {
    throw awaitingExpenseClaims.error;
  }

  if (bankAccount.isError) {
    throw bankAccount.error;
  }

  const filteredAwaitingExpenseClaims = useMemo(() => {
    if (!awaitingExpenseClaims.data) {
      return [];
    }
    const filteredExpenseClaims = filterExpenseClaims(
      awaitingExpenseClaims.data,
      filters.dateFrom,
      filters.dateTo,
      filters.claimTypes
    );
    return sortExpenseClaimsByStatus(filteredExpenseClaims);
  }, [awaitingExpenseClaims.data, filters]);

  useEffect(() => {
    if (isPollingAwaitingExpenseClaims) {
      const timer = setTimeout(() => {
        setIsPollingAwaitingExpenseClaims(false);
      }, 32000); // 32 seconds

      return () => clearTimeout(timer);
    }
  }, [isPollingAwaitingExpenseClaims]);

  useEffect(() => {
    if (dossierId && dossierId !== filters.dossierId) {
      setFilters({ ...filters, dossierId, nextPageLink: undefined });
    }
  }, [dossierId]);

  const onCancelExpenseClaim = (claimId: string) => {
    setCancellingClaimId(claimId);
  };

  const onConfirm = async () => {
    if (cancellingClaimId) {
      await _cancelExpenseClaim({ claimId: cancellingClaimId });
      setCancellingClaimId(null);
      setIsPollingAwaitingExpenseClaims(true);
    }
  };

  const inputsToFilters = (inputs: ExpenseClaimFilterInputs) => {
    return {
      ...filters,
      dateFrom: inputs.dateFrom
        ? dayjs(inputs.dateFrom).toISOString()
        : undefined,
      dateTo: inputs.dateTo
        ? dayjs(inputs.dateTo).endOf("day").toISOString()
        : undefined,
      claimTypes: inputs.category
        ? inputs.category.map((cat) => cat.id as ExpenseClaimType)
        : undefined,
    };
  };

  const renderExpenseClaimCard = (expenseClaim: ExpenseClaim) => {
    return (
      <ExpenseClaimCard
        key={expenseClaim.id}
        primaryAmount="incGst"
        expenseClaim={expenseClaim}
        faqs={faqProps}
        etaFaqAppLocationIdentifier="ExpenseClaimsView-WhatIsTheETA"
        onCancelExpenseClaim={onCancelExpenseClaim}
      />
    );
  };

  const renderItems = (items: ExpenseClaim[], paginationHistory: string[]) => {
    return (
      <>
        {paginationHistory.length === 0 &&
          filteredAwaitingExpenseClaims.map(renderExpenseClaimCard)}
        {items.map(renderExpenseClaimCard)}
      </>
    );
  };

  return (
    <>
      <APModal
        isOpen={!!cancellingClaimId}
        onClose={() => setCancellingClaimId(null)}
        closeText="Back"
        confirmButton={
          <APButton variant="contained" text="Yes" onClick={onConfirm} />
        }
      >
        This will cancel the expense claim, are you sure?
      </APModal>
      <APFilterableList
        title="Expense Claims"
        filters={filters}
        setFilters={setFilters}
        pageSize={DEFAULT_PAGE_SIZE}
        getItems={getResolvedExpenseClaims}
        inputsToFilters={inputsToFilters}
        handleSubmit={handleSubmit}
        errors={errors}
        formFields={
          <>
            <Box
              sx={{
                display: "flex",
                columnGap: 2,
              }}
            >
              <APDateField
                name="dateFrom"
                label="Date From"
                control={control}
                defaultValue={null}
                errors={errors}
                setError={setError}
                clearErrors={clearErrors}
                maxDate={dateTo ? dayjs(dateTo) : undefined}
                clearable
              />
              <APDateField
                name="dateTo"
                label="Date To"
                control={control}
                defaultValue={null}
                errors={errors}
                setError={setError}
                clearErrors={clearErrors}
                minDate={dateFrom ? dayjs(dateFrom) : undefined}
                clearable
              />
            </Box>

            <APAutoComplete
              name="category"
              label="Category"
              options={ExpenseClaimTypeOptions}
              control={control}
              validations={{ required: true }}
              errors={errors}
              defaultValue={ExpenseClaimTypeOptions}
              setValue={setValue}
              multiple
            />
          </>
        }
        renderItems={renderItems}
        isLoading={
          isLoading || !navigationCheckComplete || isCancellingExpenseClaim
        }
        isSuccess={isSuccess && navigationCheckComplete && !!dossierId}
        topCollapseContent={
          selectedDossier && (
            <Box mb={2}>
              <DossierSelector
                dossiers={activatedDossiers}
                selectedDossier={selectedDossier}
                redirectTo="dossierExpenseClaims"
                showDivider
              />
            </Box>
          )
        }
        abovePaginationContent={
          awaitingExpenseClaims.isSuccess &&
          isSuccessFaqs &&
          selectedDossier ? (
            <Box mr={2}>
              <DossierBalances
                pendingClaimsBalance={calculatePendingClaimBalance(
                  awaitingExpenseClaims.data
                )}
                availableBalance={
                  selectedDossier.availableBalanceExcludingClaims.incGst ?? 0
                }
                faqs={{
                  ...faqProps,
                }}
                faqAppLocationIdentifiers={{
                  pendingClaims: "ExpenseClaimsView-PendingClaims",
                  availableBalance: "ExpenseClaimsView-AvailForClaims",
                }}
              />
              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mt={1}
              >
                {faqProps.renderAppLocation(
                  "ExpenseClaimsView-WhatCanIClaim",
                  <Box>
                    <Typography
                      variant="bodySmall"
                      color="primary"
                      sx={{ textDecoration: "underline", cursor: "pointer" }}
                    >
                      What can I claim?
                    </Typography>
                  </Box>,
                  true
                )}
                <Box>
                  <Typography variant="bodySmall">Inc GST</Typography>
                </Box>
              </Box>
            </Box>
          ) : undefined
        }
        overrideContent={
          !selectedDossier && (
            <Typography variant="bodyMedium">
              Dossier is not activated
            </Typography>
          )
        }
        bottomContent={
          selectedDossier && (
            <Stack spacing={2} mt={2}>
              {bankAccount.isSuccess && bankAccount.data && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="titleMedium" color="secondary">
                    Claims sent to:
                  </Typography>
                  <Typography variant="titleMedium" color="secondary">
                    BSB: {bankAccount.data.bsb} Account:{" "}
                    {bankAccount.data.accountNumber}
                  </Typography>
                </Box>
              )}
              <ActiveDossierNavButtons
                dossier={selectedDossier}
                backTypes={["back", "home"]}
                navButtons={["view-budgets", "update-bank"]}
                rightNavButtons={
                  isDossierActive(selectedDossier)
                    ? ["submit-claim"]
                    : undefined
                }
              />
              <Box sx={{ display: "flex" }}>
                {faqProps.renderAppLocation(
                  "ExpenseClaimsView-WhatIsTheETA",
                  <Typography
                    variant="bodySmall"
                    color="primary"
                    sx={{ textDecoration: "underline", cursor: "pointer" }}
                  >
                    *What is the ETA?
                  </Typography>,
                  true
                )}
              </Box>
            </Stack>
          )
        }
        shouldDisplayEmptyMessage={filteredAwaitingExpenseClaims.length === 0}
      />
    </>
  );
};

export default DossierExpenseClaims;
